// Masking for PostHog session replay on the school platform. The recorder masks every text node and
// input, and calls `maskReplayText` for each one: only content inside a `data-ph-unmask` wrapper
// (the exercise question and answer, the content-feedback comment) is recorded as-is. Wired up in
// `instrumentation-client.ts`.
//
// ⚠ Masking covers text only. The recorder copies attributes as they are, so an element whose
// `href` or `aria-label` carries a name needs `data-ph-block`, which records it as an empty box.

const UNMASK_SELECTOR = '[data-ph-unmask]'

/**
 * Ids of other people in a recorded URL: a teacher viewing /school/students/123/stats records the
 * student 123. An optional origin and locale prefix come first; whatever follows the id is kept.
 * `/student/[studentId]` is hidden in the school platform (utils/schoolPlatformRoutes.js), but the
 * ticket lists it, so a 404'd hit on it is masked too.
 */
const ID_IN_URL_PATTERN =
  /^((?:https?:\/\/[^/]+)?(?:\/(?:en|no|uk))?\/(?:school\/students|student|school\/teacher\/classes))\/[^/?#]+/

/** The student's name rides in the teacher-panel stats URL (`studentStatsHref` in teacherPanel.utils.ts). */
const NAME_PARAM_PATTERN = /([?&])name=[^&#]*&?/g

/**
 * True when `element` sits inside a `data-ph-unmask` wrapper. MathLive renders a `<math-field>`'s
 * content in a shadow root, and `closest()` stops at that boundary, so on a miss we continue from
 * the shadow root's host. ⚠ Never unmask by tag: the Oracle chat input is a `<math-field>` too.
 */
export const isInsideUnmaskedArea = (element: Element | null | undefined): boolean => {
  if (!element) return false
  if (element.closest(UNMASK_SELECTOR)) return true

  const root = element.getRootNode()
  return root instanceof ShadowRoot && isInsideUnmaskedArea(root.host)
}

/** `maskTextFn` / `maskInputFn`: the real text inside an unmasked area, otherwise rrweb's own `*` masking. */
export const maskReplayText = (text: string, element?: HTMLElement): string =>
  isInsideUnmaskedArea(element) ? text : text.replace(/\S/g, '*')

/** Replaces the id segment of the routes above with `*` and removes the `name` query parameter. */
export const maskReplayUrl = (url: string): string =>
  url
    .replace(ID_IN_URL_PATTERN, '$1/*')
    .replace(NAME_PARAM_PATTERN, '$1')
    .replace(/[?&](?=#|$)/, '')
